import { rm, combineReducers } from 'utils';
import { BaseAction, baseFormReducer, BaseInitialState, baseReducer, BaseState } from '../Utils';
import {
  ImageAction,
  ImageInitialState,
  imagesReducer,
  ImageState
} from '../Components/ImageUpload/ImageHelper';
import { SupportProductModel } from 'models/SupportProductModel';
import { isCustomerContactValid } from '../Components/CustomerContact/utils';

type InternalState = BaseState &
  ImageState & {
    products: SupportProductModel[];
    productsError?: string;
    damage: string;
    damageError: string;
    packageSlip: string;
    packageSlipError: string;
    description: string;
    descriptionError: string;
    received: string;
    receivedError: string;
    solution: string;
    solutionError: string;
  };

export const shippingDamageInitialState: InternalState = {
  ...BaseInitialState,
  ...ImageInitialState,
  products: [],
  damage: '',
  damageError: '',
  packageSlip: '',
  packageSlipError: '',
  description: '',
  descriptionError: '',
  received: '',
  receivedError: '',
  solution: '',
  solutionError: ''
};

type Action =
  | { type: 'productChanged'; value: SupportProductModel }
  | { type: 'productsError'; value: string }
  | { type: 'damage'; value: string }
  | { type: 'damageError'; value: string }
  | { type: 'packageSlip'; value: string }
  | { type: 'packageSlipError'; value: string }
  | { type: 'description'; value: string }
  | { type: 'descriptionError'; value: string }
  | { type: 'received'; value: string }
  | { type: 'receivedError'; value: string }
  | { type: 'solution'; value: string }
  | { type: 'solutionError'; value: string }
  | BaseAction
  | ImageAction;

export const shippingDamageReducer = (state: InternalState, action: Action): InternalState => {
  switch (action.type) {
    case 'damage':
      return { ...state, damage: action.value };
    case 'damageError':
      return { ...state, damageError: action.value };
    case 'packageSlip':
      return { ...state, packageSlip: action.value };
    case 'packageSlipError':
      return { ...state, packageSlipError: action.value };
    case 'description':
      return { ...state, description: action.value };
    case 'descriptionError':
      return { ...state, descriptionError: action.value };
    case 'productChanged':
      return { ...state, products: getProducts(action.value, state.products) };
    case 'productsError':
      return { ...state, productsError: action.value };
    case 'received':
      return { ...state, received: action.value };
    case 'receivedError':
      return { ...state, receivedError: action.value };
    case 'solution':
      return { ...state, solution: action.value };
    case 'solutionError':
      return { ...state, solutionError: action.value };
    default:
      return state;
  }
};

export const reducer = combineReducers<InternalState, Action>(
  baseFormReducer,
  baseReducer,
  imagesReducer,
  shippingDamageReducer
);

const getProducts = (
  product: SupportProductModel,
  selectedProducts: SupportProductModel[]
): SupportProductModel[] => {
  if (selectedProducts.some(sp => sp.productId === product.productId)) {
    return selectedProducts.map(sp => {
      if (sp.productId === product.productId) {
        return { ...sp, quantity: product.quantity };
      }
      return sp;
    });
  }

  selectedProducts.push({
    productId: product.productId,
    productName: product.productName,
    productRecievedDate: product.productRecievedDate,
    quantity: product.quantity,
    unit: product.unit
  });

  return selectedProducts;
};

export const isValid = (
  state: InternalState,
  addressChanging: boolean,
  dispatch: (action: Action) => void
) => {
  let isValid = true;

  if (state.products.filter(p => p.quantity > 0).length === 0) {
    dispatch({
      type: 'productsError',
      value: rm('customerpage_returns_validation_error_products')
    });
    isValid = false;
  }

  if (addressChanging) {
    dispatch({
      type: 'addressError',
      value: rm('customerpage_returns_validation_error_address')
    });
    isValid = false;
  }

  if (state.damage === '') {
    dispatch({
      type: 'damageError',
      value: rm('customerservice_shipping_damage_error')
    });
    isValid = false;
  }

  if (state.packageSlip === '') {
    dispatch({
      type: 'packageSlipError',
      value: rm('customerservice_shipping_entry_error')
    });
    isValid = false;
  }

  if (state.received === '') {
    dispatch({
      type: 'receivedError',
      value: rm('customerservice_shipping_received_error')
    });
    isValid = false;
  }

  if (state.solution === '') {
    dispatch({
      type: 'solutionError',
      value: rm('customerservice_shipping_solution_error')
    });
    isValid = false;
  }

  if (state.images.length < 5 && state.received === rm('customerservice_shipping_received_yes')) {
    dispatch({
      type: 'imagesError',
      value: rm('customerservice_shipping_images_validation_error')
    });
    isValid = false;
  }

  isValid = isCustomerContactValid({ state, valid: isValid, dispatch });

  return isValid;
};

export const productChanged = (
  product: SupportProductModel,
  dispatch: (action: Action) => void
) => {
  dispatch({ type: 'productChanged', value: product });
  dispatch({ type: 'productsError', value: '' });
};

export const damageChange = (value: string | number, dispatch: (action: Action) => void) => {
  dispatch({
    type: 'damage',
    value: value.toString()
  });
  dispatch({
    type: 'damageError',
    value: ''
  });
};

export const packageSlipEntryChange = (
  value: string | number,
  dispatch: (action: Action) => void
) => {
  dispatch({
    type: 'packageSlip',
    value: value.toString()
  });
  dispatch({
    type: 'packageSlipError',
    value: ''
  });
};

export const receivedChange = (value: string | number, dispatch: (action: Action) => void) => {
  dispatch({
    type: 'received',
    value: value.toString()
  });
  dispatch({
    type: 'receivedError',
    value: ''
  });
};

export const solutionChange = (value: string | number, dispatch: (action: Action) => void) => {
  dispatch({
    type: 'solution',
    value: value.toString()
  });
  dispatch({
    type: 'solutionError',
    value: ''
  });
};

export const getDamageOptions = () => [
  {
    label: rm('customerservice_shipping_damage_packaging'),
    value: rm('customerservice_shipping_damage_packaging'),
    id: 'damage-001'
  },
  {
    label: rm('customerservice_shipping_damage_hidden'),
    value: rm('customerservice_shipping_damage_hidden'),
    id: 'damage-002'
  },
  {
    label: rm('customerservice_shipping_damage_driver'),
    value: rm('customerservice_shipping_damage_driver'),
    id: 'damage-003'
  }
];

export const getPackageSlipEntryOptions = () => [
  {
    label: rm('customerservice_shipping_entry_driver'),
    value: rm('customerservice_shipping_entry_driver'),
    id: 'packageSlip-001'
  },
  {
    label: rm('customerservice_shipping_entry_customer'),
    value: rm('customerservice_shipping_entry_customer'),
    id: 'packageSlip-002'
  },
  {
    label: rm('customerservice_shipping_entry_none'),
    value: rm('customerservice_shipping_entry_none'),
    id: 'packageSlip-003'
  }
];

export const getReceivedOptions = () => [
  {
    label: rm('customerservice_shipping_received_yes'),
    value: rm('customerservice_shipping_received_yes'),
    id: 'received-001'
  },
  {
    label: rm('customerservice_shipping_received_no'),
    value: rm('customerservice_shipping_received_no'),
    id: 'received-002'
  }
];

export const getSolutionRequestsOptions = () => [
  {
    label: rm('customerservice_shipping_solution_cancel'),
    value: rm('customerservice_shipping_solution_cancel'),
    id: 'solution-001'
  },
  {
    label: rm('customerservice_shipping_solution_new'),
    value: rm('customerservice_shipping_solution_new'),
    id: 'solution-002'
  },
  {
    label: rm('customerservice_shipping_solution_discount'),
    value: rm('customerservice_shipping_solution_discount'),
    id: 'solution-003'
  }
];
